// One date formatter for the whole app: every date and time the UI shows goes through here, in the
// format chosen in Settings (region + date format). Pure (no React); pages use useDateFormatter() in
// lib/format.ts, and code outside React (exports) reads currentDatePrefs().
import type { DateFmt } from '../settings/SettingsContext.js';

export type DateInput = string | number | Date | null | undefined;
export type YearMode = 'always' | 'auto' | 'never';
export interface DatePrefs { locale: string; dateFormat: DateFmt }
export interface DateOpts { year?: YearMode; weekday?: 'short' | 'long'; now?: Date; timeZone?: string }

const FMTS: readonly DateFmt[] = ['locale', 'DD MMM YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
export const DEFAULT_DATE_PREFS: DatePrefs = { locale: 'en-GB', dateFormat: 'locale' };
/** server prefs are z.string(): anything unknown falls back to the regional default. */
export const normalizeDateFmt = (v: unknown): DateFmt => (FMTS.includes(v as DateFmt) ? (v as DateFmt) : 'locale');
const prefsOf = (o?: Partial<DatePrefs>): DatePrefs => ({
  locale: o?.locale || DEFAULT_DATE_PREFS.locale,
  dateFormat: normalizeDateFmt(o?.dateFormat),
});

// ── parsing ─────────────────────────────────────────────────────────────────────────────────
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
// SQLite datetime('now'): 'YYYY-MM-DD HH:MM[:SS[.fff]]', UTC, no zone (space separator only).
const SQL_TS = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/;
export const isIsoDay = (s: unknown): s is string => typeof s === 'string' && ISO_DAY.test(s);
/** ISO-looking date or stamp ('YYYY-MM-DD', 'YYYY-MM-DD HH:MM…', 'YYYY-MM-DDTHH:MM…'). */
export const looksLikeDate = (s: unknown): s is string =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|[ T]\d{2}:\d{2})/.test(s.trim());

export interface Parsed { d: Date; calendar: boolean }
/** one parser for every date the app shows.
 *  calendar = a day with no time: 'YYYY-MM-DD' strings and Dates at exactly UTC midnight (parseOvDate,
 *  startOfTodayUtc, addWorkingDays) are read in UTC so a viewer west of UTC never sees the day before. */
export function parseStamp(input: DateInput): Parsed | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    if (Number.isNaN(+input)) return null;
    const cal = input.getUTCHours() === 0 && input.getUTCMinutes() === 0 && input.getUTCSeconds() === 0 && input.getUTCMilliseconds() === 0;
    return { d: input, calendar: cal };
  }
  if (typeof input === 'number') return Number.isFinite(input) ? { d: new Date(input), calendar: false } : null;
  const s = String(input).trim();
  let m = ISO_DAY.exec(s);
  if (m) {
    const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
    return Number.isNaN(+d) || d.getUTCDate() !== +m[3]! ? null : { d, calendar: true };
  }
  m = SQL_TS.exec(s);
  if (m) {
    const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +(m[6] ?? 0), +((m[7] ?? '0').padEnd(3, '0').slice(0, 3))));
    return Number.isNaN(+d) ? null : { d, calendar: false };
  }
  const d = new Date(s);
  return Number.isNaN(+d) ? null : { d, calendar: false };
}

// ── Intl helpers (cached: the Overview grid formats hundreds of cells per render) ────────────
const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(locale: string, o: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const k = `${locale}|${JSON.stringify(o)}`;
  let f = cache.get(k);
  if (!f) {
    try { f = new Intl.DateTimeFormat(locale, o); } catch { f = new Intl.DateTimeFormat('en-GB', o); }
    cache.set(k, f);
  }
  return f;
}
const zoneOf = (p: Parsed, timeZone?: string) => (p.calendar ? 'UTC' : timeZone);
const pad = (n: number) => String(n).padStart(2, '0');
function ymd(p: Parsed, timeZone?: string): { y: number; m: number; d: number } {
  if (p.calendar) return { y: p.d.getUTCFullYear(), m: p.d.getUTCMonth() + 1, d: p.d.getUTCDate() };
  if (!timeZone) return { y: p.d.getFullYear(), m: p.d.getMonth() + 1, d: p.d.getDate() };
  const parts = dtf('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(p.d);
  const get = (t: string) => Number(parts.find((x) => x.type === t)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}
function dropYear(p: Parsed, mode: YearMode, now: Date, timeZone?: string): boolean {
  if (mode === 'never') return true;
  if (mode === 'always') return false;
  return ymd(p, timeZone).y === now.getFullYear();
}

// ── public formatters ──────────────────────────────────────────────────────────────────────
const weekdayOf = (p: Parsed, locale: string, style: 'short' | 'long', timeZone?: string) =>
  dtf(locale, { weekday: style, timeZone: zoneOf(p, timeZone) }).format(p.d);
export function formatWeekday(input: DateInput, opts: { locale?: string; style?: 'short' | 'long'; timeZone?: string } = {}): string {
  const p = parseStamp(input);
  return p ? weekdayOf(p, opts.locale || 'en-GB', opts.style ?? 'short', opts.timeZone) : '';
}

/** the user's date. year: 'always' (default) | 'auto' (drop in the current year) | 'never'. */
export function formatDate(input: DateInput, opts: Partial<DatePrefs> & DateOpts = {}): string {
  const p = parseStamp(input);
  if (!p) return '';
  const { locale, dateFormat } = prefsOf(opts);
  const tz = zoneOf(p, opts.timeZone);
  const noYear = dropYear(p, opts.year ?? 'always', opts.now ?? new Date(), opts.timeZone);
  const { y, m, d } = ymd(p, opts.timeZone);
  let out: string;
  switch (dateFormat) {
    case 'DD/MM/YYYY': out = noYear ? `${pad(d)}/${pad(m)}` : `${pad(d)}/${pad(m)}/${y}`; break;
    case 'MM/DD/YYYY': out = noYear ? `${pad(m)}/${pad(d)}` : `${pad(m)}/${pad(d)}/${y}`; break;
    case 'YYYY-MM-DD': out = noYear ? `${pad(m)}-${pad(d)}` : `${y}-${pad(m)}-${pad(d)}`; break;
    case 'DD MMM YYYY': {
      // always day, month, year whatever the region's own order; month name from the region
      const parts = dtf(locale, { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }).formatToParts(p.d);
      const mon = parts.find((x) => x.type === 'month')?.value ?? pad(m);
      out = noYear ? `${pad(d)} ${mon}` : `${pad(d)} ${mon} ${y}`;
      break;
    }
    case 'locale':
    default:
      // unchanged from today's output: the region's own order (en-US "Mar 09, 2026", de-DE "09. März 2026")
      out = dtf(locale, noYear
        ? { day: '2-digit', month: 'short', timeZone: tz }
        : { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }).format(p.d);
  }
  return opts.weekday ? `${weekdayOf(p, locale, opts.weekday, opts.timeZone)} ${out}` : out;
}

/** 24-hour time in the region (hourCycle h23 everywhere, so el-GR/en-US match the rest of the app).
 *  A stored 'HH:MM' string (agenda entries) passes through unchanged. */
export function formatTime(input: DateInput, opts: { locale?: string; seconds?: boolean; timeZone?: string } = {}): string {
  if (typeof input === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(input.trim())) return input.trim();
  const p = parseStamp(input);
  if (!p || (p.calendar && typeof input === 'string')) return '';
  return dtf(opts.locale || 'en-GB', {
    hour: '2-digit', minute: '2-digit', ...(opts.seconds ? { second: '2-digit' } : {}), hourCycle: 'h23', timeZone: opts.timeZone,
  }).format(p.d);
}

/** "<date>, HH:mm". A bare 'YYYY-MM-DD' has no time, so it renders as the date alone. */
export function formatDateTime(input: DateInput, opts: Partial<DatePrefs> & { seconds?: boolean; timeZone?: string; year?: YearMode; now?: Date } = {}): string {
  const p = parseStamp(input);
  if (!p) return '';
  if (p.calendar && typeof input === 'string') return formatDate(input, opts);
  const asInstant: Parsed = { d: p.d, calendar: false }; // a UTC-midnight instant is still an instant here
  const date = formatDate(asInstant.d.getTime(), opts);
  return `${date}, ${formatTime(asInstant.d, { locale: opts.locale, seconds: opts.seconds, timeZone: opts.timeZone })}`;
}

/** month label. Numeric formats give "10/2026" / "2026-10"; a month name whenever year:false or style:'long'
 *  (a bare "10" on an axis is unreadable). Accepts 'YYYY-MM'. */
export function formatMonth(input: DateInput, opts: Partial<DatePrefs> & { year?: boolean; style?: 'short' | 'long' } = {}): string {
  const src = typeof input === 'string' && /^\d{4}-\d{2}$/.test(input) ? `${input}-01` : input;
  const p = parseStamp(src);
  if (!p) return typeof input === 'string' ? input : '';
  const { locale, dateFormat } = prefsOf(opts);
  const withYear = opts.year !== false;
  const tz = zoneOf(p);
  const { y, m } = ymd(p);
  if (withYear && opts.style !== 'long') {
    if (dateFormat === 'DD/MM/YYYY' || dateFormat === 'MM/DD/YYYY') return `${pad(m)}/${y}`;
    if (dateFormat === 'YYYY-MM-DD') return `${y}-${pad(m)}`;
  }
  return dtf(locale, { month: opts.style ?? 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: tz }).format(p.d);
}

/** "29/09 - 05/10/2026"; both years when they differ. Plain hyphen, as the Schedule uses today. */
export function formatDateRange(start: DateInput, end: DateInput, opts: Partial<DatePrefs> = {}): string {
  const a = parseStamp(start), b = parseStamp(end);
  if (!a || !b) return formatDate(start ?? end, opts);
  // ISO is never shortened inside a range ("09-29 - 2026-10-05" reads as garbage)
  const sameYear = ymd(a).y === ymd(b).y && prefsOf(opts).dateFormat !== 'YYYY-MM-DD';
  return `${formatDate(start, { ...opts, year: sameYear ? 'never' : 'always' })} - ${formatDate(end, opts)}`;
}

/** "just now", "5m ago", "3h ago", "4d ago", then the date (year when not this year). */
export function formatAgo(input: DateInput, opts: Partial<DatePrefs> & { now?: number; maxDays?: number } = {}): string {
  const p = parseStamp(input);
  if (!p) return '';
  const mins = Math.round(((opts.now ?? Date.now()) - +p.d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days <= (opts.maxDays ?? 7)) return `${days}d ago`;
  return formatDate(p.d.getTime(), { ...opts, year: 'auto', now: opts.now != null ? new Date(opts.now) : undefined });
}

/** a Smartsheet cell for display: DATE/DATETIME → the user's date; booleans → ✓; else the text. */
export function formatSheetCell(type: string | undefined, value: unknown, opts: Partial<DatePrefs> = {}): string {
  if (value === true) return '✓';
  if (value == null || value === false) return '';
  const s = String(value);
  if ((type === 'DATE' || type === 'DATETIME') && looksLikeDate(s)) return formatDate(s.slice(0, 10), opts) || s;
  return s;
}
/** any value that may hold an ISO date (history pills, free-text date columns): format it if it is one. */
export function formatMaybeDate(value: unknown, opts: Partial<DatePrefs> = {}): string {
  const s = value == null ? '' : String(value);
  if (isIsoDay(s.trim())) return formatDate(s.trim(), opts) || s;
  if (looksLikeDate(s)) return formatDateTime(s.trim(), opts) || s;
  return s;
}

/** Excel number format matching the preference, for real date cells in .xlsx exports. */
export function excelDateFormat(prefs: Partial<DatePrefs> = {}): string {
  const { locale, dateFormat } = prefsOf(prefs);
  switch (dateFormat) {
    case 'DD/MM/YYYY': return 'dd/mm/yyyy';
    case 'MM/DD/YYYY': return 'mm/dd/yyyy';
    case 'YYYY-MM-DD': return 'yyyy-mm-dd';
    case 'DD MMM YYYY': return 'dd mmm yyyy';
    default: return locale === 'en-US' ? 'mmm dd, yyyy' : 'dd mmm yyyy';
  }
}
/** a calendar date as the LOCAL-midnight Date SheetJS expects (its datenum() corrects by the local offset). */
export function toExcelDate(input: DateInput): Date | '' {
  const p = parseStamp(input);
  if (!p) return '';
  const { y, m, d } = ymd(p);
  return new Date(y, m - 1, d);
}

/** today's LOCAL calendar day as 'YYYY-MM-DD' (replaces new Date().toISOString().slice(0, 10), the UTC day). */
export function localTodayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** the fixed stamp written INTO shared comments (Pdi, Ds): never the viewer's preference. "6 Oct 2026". */
const EN_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function commentStamp(now: Date = new Date()): string {
  return `${now.getDate()} ${EN_MON[now.getMonth()]} ${now.getFullYear()}`;
}
/** fixed, unambiguous date for text a CLIENT reads (emails, proforma): "9 October 2026". */
const EN_MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function formatClientDate(input: DateInput): string {
  const p = parseStamp(input);
  if (!p) return typeof input === 'string' ? input : '';
  const { y, m, d } = ymd(p);
  return `${d} ${EN_MONTH[m - 1]} ${y}`;
}

/** non-hook prefs for code outside React (export helpers' default param). */
export function currentDatePrefs(): DatePrefs {
  try {
    const p = JSON.parse(localStorage.getItem('rolodex.prefs') || '{}') as { region?: string; dateFormat?: string };
    return prefsOf({ locale: p.region, dateFormat: p.dateFormat as DateFmt });
  } catch {
    return { ...DEFAULT_DATE_PREFS };
  }
}

export interface DateFormatter {
  prefs: DatePrefs;
  date(i: DateInput, o?: DateOpts): string;
  time(i: DateInput, o?: { seconds?: boolean; timeZone?: string }): string;
  dateTime(i: DateInput, o?: { seconds?: boolean; timeZone?: string; year?: YearMode }): string;
  month(i: DateInput, o?: { year?: boolean; style?: 'short' | 'long' }): string;
  weekday(i: DateInput, style?: 'short' | 'long'): string;
  range(a: DateInput, b: DateInput): string;
  ago(i: DateInput): string;
  cell(type: string | undefined, v: unknown): string;
  maybe(v: unknown): string;
}
export function makeDateFormatter(prefs: Partial<DatePrefs>): DateFormatter {
  const P = prefsOf(prefs);
  return {
    prefs: P,
    date: (i, o) => formatDate(i, { ...P, ...o }),
    time: (i, o) => formatTime(i, { locale: P.locale, ...o }),
    dateTime: (i, o) => formatDateTime(i, { ...P, ...o }),
    month: (i, o) => formatMonth(i, { ...P, ...o }),
    weekday: (i, style) => formatWeekday(i, { locale: P.locale, style }),
    range: (a, b) => formatDateRange(a, b, P),
    ago: (i) => formatAgo(i, P),
    cell: (t, v) => formatSheetCell(t, v, P),
    maybe: (v) => formatMaybeDate(v, P),
  };
}
